import {ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import * as moment from 'moment';

import {DISPLAY_TYPE} from '../../../app.constants';
import {LocalizationService} from '../../localization/localization.service';
import {HrCalendarDay, HrCalendarTag, HrCalendarWorkScheduleDay} from '../hr-calendar.models';

@Component({
  selector: 'gp-hr-calendar-day-cell',
  templateUrl: './hr-calendar-day-cell.component.html',
  styleUrls: ['./hr-calendar-day-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HrCalendarDayCellComponent implements OnChanges {

  @Input()
  public day: HrCalendarDay;

  @Input()
  public tags: HrCalendarTag[] = [];

  @Input()
  public workSchedule: HrCalendarWorkScheduleDay | null = null;

  public workScheduleLabel: string = null;

  @HostBinding('class.hr-calendar-day')
  public readonly hostClass = true;

  constructor(private translate: TranslateService,
              private localization: LocalizationService) {
  }

  @HostBinding('class.hr-calendar-day--weekend')
  public get isWeekend(): boolean {
    return !!this.day?.isWeekend;
  }

  @HostBinding('class.hr-calendar-day--non-working')
  public get isNonWorkingDay(): boolean {
    return this.workSchedule?.isWorkingDay === false;
  }

  @HostBinding('class.hr-calendar-day--outside')
  public get isOutside(): boolean {
    return !!this.day && !this.day.inMonth;
  }

  @HostBinding('class.hr-calendar-day--today')
  public get isToday(): boolean {
    return !!this.day?.isToday;
  }

  @HostBinding('attr.aria-label')
  public get ariaLabel(): string {
    if (!this.day) {
      return null;
    }
    return [moment(this.day.date).format('dddd LL'), this.workScheduleLabel].filter(Boolean).join(', ');
  }

  @HostBinding('attr.aria-current')
  public get ariaCurrent(): string {
    return this.day?.isToday ? 'date' : null;
  }

  public ngOnChanges(): void {
    this.workScheduleLabel = this.buildWorkScheduleLabel();
  }

  public trackTag(index: number, tag: HrCalendarTag): string {
    return `${index}-${tag.label}`;
  }

  private buildWorkScheduleLabel(): string {
    if (!this.workSchedule) {
      return null;
    }
    if (!this.workSchedule.isWorkingDay) {
      return this.translate.instant('HR_CALENDAR.non_working_day');
    }

    const hours = this.localization.localizeNumber(this.workSchedule.hours, DISPLAY_TYPE.DECIMAL_UP_TO_4_DIGIT);
    return this.translate.instant('HR_CALENDAR.work_day_hours', {hours});
  }
}

